import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { GetApiKeyDto } from "services/Dtos/ApiKeyDtos";
import { getAllApiKeys } from "helpers/entitySearch";

interface ApiKeySelectProps {
  id: string;
  customerId?: string | number;
  // Selected API key id, or "" for none / all keys.
  value: string;
  onChange: (apiKeyId: string) => void;
  label?: string;
  emptyLabel?: string;
  // Stacked: a regular form field (label above). Default: compact toolbar dropdown, label for screen readers only.
  stacked?: boolean;
}

// Dropdown listing every API key of a company as "name (value)".
const ApiKeySelect: React.FC<ApiKeySelectProps> = ({
  id,
  customerId,
  value,
  onChange,
  label = "API Key",
  emptyLabel = "All API keys",
  stacked = false,
}) => {
  const [options, setOptions] = useState<GetApiKeyDto[] | null>(null);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setOptions(null);
    getAllApiKeys(Number(customerId))
      .then((keys) => {
        if (!cancelled) setOptions(keys);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Failed to load API keys");
        setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const select = (
    <select
      id={id}
      className={stacked ? "form-select" : "form-select form-select-sm"}
      style={stacked ? undefined : { minWidth: 280 }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={options === null}
    >
      <option value="">{options === null ? "Loading API keys..." : emptyLabel}</option>
      {options?.map((apiKey) => (
        <option key={apiKey.id} value={String(apiKey.id)}>
          {apiKey.name} ({apiKey.key})
        </option>
      ))}
    </select>
  );

  if (stacked) {
    return (
      <div className="mb-3">
        <label htmlFor={id} className="form-label fw-bold">{label}</label>
        {select}
      </div>
    );
  }

  return (
    <div>
      <label htmlFor={id} className="visually-hidden">
        {label}
      </label>
      {select}
    </div>
  );
};

export default ApiKeySelect;
